import { useAppDispatch, useAppSelector } from 'app/hooks';
import TextKey from 'components/common/text-key';
import AddExistingSystemButton from 'components/existing-equipment-page/add-existing-system-button';
import ExistingSystemSection from 'components/existing-equipment-page/existing-system-section';
import PtacExistingUnitsCard from 'components/existing-equipment-page/ptac-existing-units-card';
import ExistingEquipmentList from 'components/structure-page/lists/existing-equipment-list';
import AddEquipmentModal from 'components/structure-page/modals/add-equipment-modal';
import { useFooterConfig } from 'contexts/footer-context';
import { setActiveTab } from 'features/ui';
import useDuctedFilterContextReset from 'hooks/use-ducted-filter-context-reset';
import useDuctedPrewarm from 'hooks/use-ducted-prewarm';
import useEnsureAnchorExistingSystem from 'hooks/use-ensure-anchor-existing-system';
import useHybridStructureAutobuild from 'hooks/use-hybrid-structure-autobuild';
import useIsPtacPathway from 'hooks/use-is-ptac-pathway';
import useSystemsAwaitingFirstSave from 'hooks/use-systems-awaiting-first-save';
import { useCallback, useMemo } from 'react';
import { isPtacExistingSystem } from 'utils/equipment-preferences-complete';
import {
  equipmentStepProceedMessage,
  EXISTING_EQUIPMENT_PAGE_TITLE,
  EXISTING_EQUIPMENT_PAGE_TITLE_KEY,
  EXISTING_PTAC_PAGE_TITLE,
  EXISTING_PTAC_PAGE_TITLE_KEY
} from 'utils/existing-equipment-steps';
import { warningModal } from 'utils/ui';

export default function ExistingEquipmentPage() {
  const dispatch = useAppDispatch();
  const { existingSystems } = useAppSelector((state) => state.structure);
  const existingEquipmentProgress = useAppSelector(
    (state) => state.ui.pageProgress.existingEquipment
  );
  const isPtac = useIsPtacPathway();

  const existingEquipmentIncomplete = existingEquipmentProgress !== 100;
  const proceedMessage = equipmentStepProceedMessage({
    tab: 'existingEquipment',
    existingSystems
  });

  useEnsureAnchorExistingSystem();
  useDuctedPrewarm();
  useHybridStructureAutobuild();
  useDuctedFilterContextReset();

  const awaitingFirstSave = useSystemsAwaitingFirstSave();

  const handleBackToProject = useCallback(() => {
    dispatch(setActiveTab('project'));
  }, [dispatch]);

  const handleNext = useCallback(() => {
    if (existingEquipmentIncomplete) {
      warningModal(proceedMessage);
      return;
    }
    dispatch(setActiveTab('equipmentPreferences'));
  }, [dispatch, existingEquipmentIncomplete, proceedMessage]);

  useFooterConfig(
    useMemo(
      () => ({
        onBack: handleBackToProject,
        onNext: handleNext,
        backText: 'Back',
        nextText: 'Next',
        nextDisabled: existingEquipmentIncomplete,
        nextDisabledReason: existingEquipmentIncomplete
          ? proceedMessage
          : undefined,
        nextVariant: 'green' as const,
        showBackArrow: true
      }),
      [
        existingEquipmentIncomplete,
        handleBackToProject,
        handleNext,
        proceedMessage
      ]
    )
  );

  const preparing = (
    <div className='mb-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500'>
      Preparing recommendations…
    </div>
  );

  const hasMultipleSystems = existingSystems.length > 1;
  const systemSections = existingSystems.map((system) => (
    <ExistingSystemSection
      key={system.uniqueId}
      system={system}
      showHeader={hasMultipleSystems}
      removable={hasMultipleSystems}
      awaitingFirstSave={awaitingFirstSave(system.uniqueId)}
      preparing={preparing}
    />
  ));

  if (isPtac) {
    const anchor = existingSystems.find(isPtacExistingSystem) ?? null;
    return (
      <div className='mx-auto flex h-full max-w-[1400px] flex-col'>
        <h2 className='w-full pb-8 pt-5 text-center text-4xl font-semibold text-gray-900'>
          <TextKey
            code={EXISTING_PTAC_PAGE_TITLE_KEY}
            fallback={EXISTING_PTAC_PAGE_TITLE}
          />
        </h2>
        <div className='flex w-full flex-col gap-4 pb-5'>
          {!anchor || awaitingFirstSave(anchor.uniqueId) ? (
            preparing
          ) : (
            <PtacExistingUnitsCard systemUniqueId={anchor.uniqueId} />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex h-full max-w-[1400px] flex-col'>
      <h2 className='w-full pb-8 pt-5 text-center text-4xl font-semibold text-gray-900'>
        <TextKey
          code={EXISTING_EQUIPMENT_PAGE_TITLE_KEY}
          fallback={EXISTING_EQUIPMENT_PAGE_TITLE}
        />
      </h2>
      <div className='flex min-h-0 grow flex-col gap-5 pb-5'>
        {existingSystems.length === 0 ? (
          <ExistingEquipmentList />
        ) : (
          <>
            {systemSections}
            <AddExistingSystemButton />
          </>
        )}
      </div>
      <AddEquipmentModal />
    </div>
  );
}
